import { useCallback, useEffect, useRef, useState } from 'react'

const INITIAL_META = { page: 1, pages: 1, count: 0 }

const usePaginatedGrid = ({ fetcher, rowsKey, defaultPerPage, filtersKey = '' }) => {
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState(INITIAL_META)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(defaultPerPage)
  const [isLoading, setIsLoading] = useState(true)

  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher
  const versionRef = useRef(0)
  const prevFiltersKeyRef = useRef(filtersKey)

  const load = useCallback(async() => {
    const version = ++versionRef.current
    setIsLoading(true)
    try {
      const response = await fetcherRef.current({ page, perPage })
      if (version !== versionRef.current)
        return
      setRows(response[rowsKey] || [])
      setMeta(response.pagy || INITIAL_META)
    } catch {
      if (version === versionRef.current) {
        setRows([])
        setMeta(INITIAL_META)
      }
    } finally {
      if (version === versionRef.current)
        setIsLoading(false)
    }
  }, [rowsKey, page, perPage])

  // On filter change, reset page first; the page change re-runs this effect.
  useEffect(() => {
    const filtersChanged = prevFiltersKeyRef.current !== filtersKey
    prevFiltersKeyRef.current = filtersKey
    if (filtersChanged && page !== 1) {
      setPage(1)
      return
    }
    load()
  }, [load, filtersKey])

  const handlePerPageChange = useCallback(nextPerPage => {
    setPerPage(nextPerPage)
    setPage(1)
  }, [])

  return { rows, meta, page, perPage, isLoading, setPage, setPerPage: handlePerPageChange }
}

export default usePaginatedGrid
